import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import axios from 'axios';
import { ExternalLink, CheckCircle, ArrowLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProduct();
  }, [slug]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${slug}`);
      setProduct(response.data);
    } catch (error) {
      console.error('Failed to fetch product:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex min-h-screen items-center justify-center">Loading...</div>;
  }

  if (!product) {
    return <div className="flex min-h-screen items-center justify-center">Product not found</div>;
  }

  const faqs = [
    {
      q: language === 'ro' ? 'Cum primesc produsul?' : 'How do I receive the product?',
      a: language === 'ro' 
        ? 'După achiziție, vei primi un email cu link-ul de descărcare instant.'
        : 'After purchase, you will receive an email with instant download link.'
    },
    {
      q: language === 'ro' ? 'Este potrivit pentru începători?' : 'Is it suitable for beginners?',
      a: language === 'ro'
        ? 'Absolut! Produsele noastre sunt create pentru toți, de la începători la avansați.'
        : 'Absolutely! Our products are designed for everyone, from beginners to advanced.'
    },
    {
      q: language === 'ro' ? 'Pot accesa produsul pe mai multe dispozitive?' : 'Can I access the product on multiple devices?',
      a: language === 'ro'
        ? 'Da, poți accesa produsul de pe orice dispozitiv după ce primești link-ul de descărcare.'
        : 'Yes, you can access the product from any device once you receive the download link.'
    }
  ];

  return (
    <div className="page-container min-h-screen py-12 bg-gradient-to-br from-white via-mint-50 to-blue-50">
      <div className="container mx-auto px-4">
        <Button
          variant="ghost"
          className="mb-8"
          onClick={() => navigate('/products')}
          data-testid="back-button"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {language === 'ro' ? 'Înapoi la Produse' : 'Back to Products'}
        </Button>

        <div className="grid gap-12 lg:grid-cols-2">
          {/* Left: Image & CTA */}
          <div>
            {product.image_url && (
              <img
                src={product.image_url}
                alt={product.title}
                className="mb-6 w-full rounded-2xl shadow-xl"
              />
            )}
            <Card className="border-2 border-teal-600 bg-gradient-to-br from-teal-50 to-white">
              <CardContent className="p-8 text-center">
                <div className="mb-4 text-4xl font-bold text-teal-600">{product.price}</div>
                <Button
                  size="lg"
                  className="w-full bg-teal-600 text-lg hover:bg-teal-700"
                  onClick={() => window.open(product.external_url, '_blank')}
                  data-testid="get-access-button"
                >
                  {t('products.getAccess')}
                  <ExternalLink className="ml-2 h-5 w-5" />
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Right: Details */}
          <div>
            {product.badge && (
              <Badge className="mb-4 bg-teal-600 text-lg">{product.badge}</Badge>
            )}
            <h1 className="mb-6 text-4xl font-bold text-slate-900 sm:text-5xl" data-testid="product-title">
              {product.title}
            </h1>
            <p className="mb-8 text-lg text-slate-600">{product.description}</p>

            {/* What's Included */}
            <div className="mb-8">
              <h2 className="mb-4 text-2xl font-bold text-slate-900">
                {language === 'ro' ? 'Ce primești:' : "What's included:"}
              </h2>
              <ul className="space-y-3">
                {product.hero_points.map((point, idx) => (
                  <li key={idx} className="flex items-start">
                    <CheckCircle className="mr-3 mt-1 h-5 w-5 flex-shrink-0 text-teal-600" />
                    <span className="text-slate-700">{point}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Who it's for */}
            <div className="mb-8">
              <h2 className="mb-4 text-2xl font-bold text-slate-900">
                {language === 'ro' ? 'Pentru cine este?' : "Who it's for:"}
              </h2>
              <p className="text-slate-600">
                {language === 'ro'
                  ? 'Acest produs este perfect pentru oricine dorește să-și construiască o bază solidă pentru libertatea financiară, indiferent dacă abia începe sau este deja pe drum.'
                  : "This product is perfect for anyone looking to build a solid foundation for financial freedom, whether you're just starting or already on your journey."}
              </p>
            </div>

            {/* FAQ */}
            <div>
              <h2 className="mb-4 text-2xl font-bold text-slate-900">FAQ</h2>
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, idx) => (
                  <AccordionItem key={idx} value={`item-${idx}`}>
                    <AccordionTrigger className="text-left text-slate-900">
                      {faq.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-slate-600">
                      {faq.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;